import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  CheckCircle2, XCircle, Info, RefreshCw, ChevronDown, ChevronRight,
  Pencil, ShieldCheck, AlertTriangle, Send, ShieldX, X,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  ProfileEditor, EnrolmentEditor, VendorsEditor, CchsEditor,
} from "@/components/poshan/SetupProgressEditors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_LABEL = {
  profile: "School Profile",
  enrolment: "Class Enrolment",
  vendors: "Vendors",
  cchs: "Cook-cum-Helpers",
};

// Pre-filled Odia + English "Please update" message templates per section.
// Admin can tweak inside the composer before sending.
const UPDATE_TEMPLATES = {
  profile: {
    subject: "School Profile Update Needed · ସ୍କୁଲ୍ ପ୍ରୋଫାଇଲ୍ ଅପଡେଟ୍ ଆବଶ୍ୟକ",
    body: "ନମସ୍କାର! ଆପଣଙ୍କ ସ୍କୁଲ୍ ପ୍ରୋଫାଇଲ୍ ରେ କିଛି ତଥ୍ୟ ସଠିକ୍ ନୁହେଁ ଅଥବା ଅସଂପୂର୍ଣ୍ଣ। ଦୟାକରି Setup → School Profile ଖୋଲି district, block, village, post ଓ pincode ସବୁ ସଠିକ ଭାବେ ଅପଡେଟ୍ କରନ୍ତୁ। ଧନ୍ୟବାଦ! 🙏",
  },
  enrolment: {
    subject: "Class Enrolment Update Needed · କ୍ଲାସ୍ ଏନ୍ରୋଲ୍‌ମେଣ୍ଟ ଅପଡେଟ୍",
    body: "ନମସ୍କାର! ଆପଣଙ୍କ ସ୍କୁଲ୍‌ର କ୍ଲାସ୍ ଏନ୍ରୋଲ୍‌ମେଣ୍ଟ ତଥ୍ୟ ଅସମ୍ପୂର୍ଣ୍ଣ ଲାଗୁଛି। ଦୟାକରି Setup → Enrolment ଟ୍ୟାବ୍ ଖୋଲି Balvatika, Primary, Upper Primary ଓ High School ର ସଠିକ୍ ସଂଖ୍ୟା ଅପଡେଟ୍ କରନ୍ତୁ। ଧନ୍ୟବାଦ! 🙏",
  },
  vendors: {
    subject: "Vendor List Update Needed · ଭେଣ୍ଡର୍ ତାଲିକା ଅପଡେଟ୍",
    body: "ନମସ୍କାର! ଆପଣଙ୍କ ସ୍କୁଲ୍‌ର ଭେଣ୍ଡର୍ ତାଲିକାରେ କିଛି ତଥ୍ୟ ଅସଂପୂର୍ଣ୍ଣ ଅଛି। ଦୟାକରି Setup → Vendors ଖୋଲି ସମସ୍ତ ଭେଣ୍ଡରଙ୍କ ନାମ, ଠିକଣା ଓ ଫୋନ୍ ନମ୍ବର ସଠିକ୍ ଭାବେ ଅପଡେଟ୍ କରନ୍ତୁ। ଧନ୍ୟବାଦ! 🙏",
  },
  cchs: {
    subject: "Cook-cum-Helper List Update Needed · CCH ତାଲିକା ଅପଡେଟ୍",
    body: "ନମସ୍କାର! ଆପଣଙ୍କ ସ୍କୁଲ୍‌ର CCH (Cook-cum-Helper) ତାଲିକାରେ କିଛି ତଥ୍ୟ ସଠିକ୍ ନୁହେଁ। ଦୟାକରି Setup → Cook-cum-Helpers ଖୋଲି ସମସ୍ତ CCH ଙ୍କ ନାମ, bank account ଓ category ସଠିକ୍ ଭାବେ ଅପଡେଟ୍ କରନ୍ତୁ। ଧନ୍ୟବାଦ! 🙏",
  },
};

function ProgressPill({ percent, done, total, onClick, testId }) {
  const tone = percent === 100
    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : percent >= 50
      ? "bg-ochre/15 text-ochre border-ochre/40"
      : percent > 0
        ? "bg-amber-100 text-amber-800 border-amber-300"
        : "bg-rose-100 text-rose-800 border-rose-300";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex min-w-[88px] items-center justify-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[0.7rem] font-semibold hover:opacity-80 ${tone}`}
      title="Click to see setup progress detail"
    >
      <Info className="h-3 w-3" />
      {done}/{total} · {percent}%
    </button>
  );
}

// Renders the read-only data for a given section.
function SectionData({ section, details }) {
  const profile = details?.profile || {};
  if (section === "profile") {
    const rows = [
      ["School Name", profile.schoolName],
      ["UDISE", profile.udiseCode],
      ["Category", profile.schoolCategory || profile.category],
      ["District", profile.district],
      ["Block", profile.block],
      ["Village", profile.village],
      ["Post", profile.post],
      ["Pincode", profile.pincode],
      ["Mobile", profile.mobile || details?.user?.mobile],
    ];
    return (
      <dl className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-white p-3 text-xs" data-testid="section-data-profile">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt className="text-[0.6rem] uppercase tracking-wide text-ink-700">{k}</dt>
            <dd className="font-medium text-ink-900">{v || <span className="italic text-rose-500">— missing —</span>}</dd>
          </div>
        ))}
      </dl>
    );
  }
  if (section === "enrolment") {
    const bv = profile.balVatikaEnrollment || 0;
    const pr = profile.primaryEnrollment || 0;
    const up = profile.upperPrimaryEnrollment || 0;
    const hs = profile.highSchoolEnrollment || 0;
    const total = Number(bv) + Number(pr) + Number(up) + Number(hs);
    return (
      <div className="overflow-hidden rounded-lg border border-border bg-white text-xs" data-testid="section-data-enrolment">
        <table className="w-full">
          <thead className="bg-sand-100 text-[0.65rem] uppercase tracking-wide text-ink-700">
            <tr>
              <th className="px-3 py-2 text-left">Class group</th>
              <th className="px-3 py-2 text-right">Students</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            <tr><td className="px-3 py-1.5">Balvatika</td><td className="px-3 py-1.5 text-right font-medium">{bv}</td></tr>
            <tr><td className="px-3 py-1.5">Primary (I–V)</td><td className="px-3 py-1.5 text-right font-medium">{pr}</td></tr>
            <tr><td className="px-3 py-1.5">Upper Primary (VI–VIII)</td><td className="px-3 py-1.5 text-right font-medium">{up}</td></tr>
            <tr><td className="px-3 py-1.5">High School (IX–X)</td><td className="px-3 py-1.5 text-right font-medium">{hs}</td></tr>
            <tr className="bg-emerald-50">
              <td className="px-3 py-1.5 font-bold">Total</td>
              <td className="px-3 py-1.5 text-right font-bold text-emerald-700">{total}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
  if (section === "vendors") {
    const rows = details?.vendors || [];
    if (rows.length === 0) {
      return <p className="rounded-lg border border-dashed border-rose-300 bg-rose-50 px-3 py-3 text-xs text-rose-700" data-testid="section-data-vendors-empty">No vendors added yet.</p>;
    }
    return (
      <div className="overflow-hidden rounded-lg border border-border bg-white text-xs" data-testid="section-data-vendors">
        <table className="w-full">
          <thead className="bg-sand-100 text-[0.65rem] uppercase tracking-wide text-ink-700">
            <tr>
              <th className="px-2 py-1.5 text-left">#</th>
              <th className="px-2 py-1.5 text-left">Name</th>
              <th className="px-2 py-1.5 text-left">Category</th>
              <th className="px-2 py-1.5 text-left">Village / Post</th>
              <th className="px-2 py-1.5 text-left">Phone</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((v, i) => (
              <tr key={v.id}>
                <td className="px-2 py-1.5 text-ink-700">{i + 1}</td>
                <td className="px-2 py-1.5 font-medium">{v.name || <span className="italic text-rose-500">—</span>}</td>
                <td className="px-2 py-1.5 capitalize">{v.category || "—"}</td>
                <td className="px-2 py-1.5">{[v.village, v.post].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-2 py-1.5">{v.phone || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (section === "cchs") {
    const rows = details?.cchs || [];
    if (rows.length === 0) {
      return <p className="rounded-lg border border-dashed border-rose-300 bg-rose-50 px-3 py-3 text-xs text-rose-700" data-testid="section-data-cchs-empty">No cook-cum-helpers added yet.</p>;
    }
    return (
      <div className="overflow-hidden rounded-lg border border-border bg-white text-xs" data-testid="section-data-cchs">
        <table className="w-full">
          <thead className="bg-sand-100 text-[0.65rem] uppercase tracking-wide text-ink-700">
            <tr>
              <th className="px-2 py-1.5 text-left">#</th>
              <th className="px-2 py-1.5 text-left">Name</th>
              <th className="px-2 py-1.5 text-left">Gender</th>
              <th className="px-2 py-1.5 text-left">Category</th>
              <th className="px-2 py-1.5 text-left">Bank A/C</th>
              <th className="px-2 py-1.5 text-left">Phone</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((c, i) => (
              <tr key={c.id}>
                <td className="px-2 py-1.5 text-ink-700">{i + 1}</td>
                <td className="px-2 py-1.5 font-medium">{c.name || <span className="italic text-rose-500">—</span>}</td>
                <td className="px-2 py-1.5">{c.gender || "—"}</td>
                <td className="px-2 py-1.5">{c.category || "—"}</td>
                <td className="px-2 py-1.5 font-mono">{c.bankAccount || "—"}</td>
                <td className="px-2 py-1.5">{c.phone || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
}

// Action footer rendered below the data table inside an expanded section.
function SectionActions({ section, row, details, verified, editing, onStartEdit, onToggleVerified, onMessageSent }) {
  const [composerOpen, setComposerOpen] = useState(false);
  const [subject, setSubject] = useState(UPDATE_TEMPLATES[section].subject);
  const [body, setBody] = useState(UPDATE_TEMPLATES[section].body);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const sendUpdateAlert = async () => {
    if (!row?.schoolId) { toast.error("School link missing"); return; }
    const cleanBody = (body || "").trim();
    if (!cleanBody) { toast.error("Message body cannot be empty"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/inbox/admin/send`, {
        schoolId: row.schoolId,
        subject: subject || UPDATE_TEMPLATES[section].subject,
        body: cleanBody,
      });
      toast.success(`Update alert sent to ${row.schoolName}`);
      setComposerOpen(false);
      onMessageSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the alert");
    } finally { setSending(false); }
  };

  const toggleVerified = async () => {
    setVerifying(true);
    try {
      await axios.post(`${API}/admin/setup-progress/${row.userId}/verify-section`, {
        section,
        verified: !verified,
      });
      toast.success(verified ? "Verification removed" : `${SECTION_LABEL[section]} verified ✓`);
      onToggleVerified?.(!verified);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update verification");
    } finally { setVerifying(false); }
  };

  return (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-sand-50 px-3 py-2" data-testid={`section-actions-${section}`}>
        <Button
          size="sm"
          variant={editing ? "default" : "outline"}
          onClick={onStartEdit}
          disabled={editing}
          className={`h-7 gap-1 text-xs ${editing ? "bg-clay text-white" : ""}`}
          data-testid={`section-${section}-edit-btn`}
        >
          <Pencil className="h-3.5 w-3.5" /> {editing ? "Editing…" : "Edit / Update"}
        </Button>
        <Button
          size="sm"
          variant={verified ? "outline" : "default"}
          onClick={toggleVerified}
          disabled={verifying}
          className={`h-7 gap-1 text-xs ${verified ? "border-emerald-400 text-emerald-700 hover:bg-emerald-50" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
          data-testid={`section-${section}-verify-btn`}
        >
          {verified ? <ShieldX className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
          {verified ? "Un-verify" : "Mark Verified"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setComposerOpen(true)}
          className="h-7 gap-1 border-rose-300 text-xs text-rose-700 hover:bg-rose-50"
          data-testid={`section-${section}-alert-btn`}
        >
          <AlertTriangle className="h-3.5 w-3.5" /> Need to update
        </Button>
      </div>

      {/* Composer dialog — opens on top of the parent Dialog (z-50). We
          intentionally let the parent stay open in the background. */}
      <Dialog open={composerOpen} onOpenChange={setComposerOpen}>
        <DialogContent className="max-w-lg" data-testid={`section-${section}-alert-composer`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-rose-600" />
              Send update alert · {SECTION_LABEL[section]}
            </DialogTitle>
            <DialogDescription>
              {row?.schoolName} · UDISE {row?.udiseCode}. Edit the pre-filled message before sending.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-ink-700">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid={`section-${section}-alert-subject`}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-ink-700">Message</label>
              <Textarea
                rows={6}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="font-[var(--font-odia)]"
                data-testid={`section-${section}-alert-body`}
              />
            </div>
            <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[0.7rem] text-rose-700">
              ⚠️ The school will see this as an eye-catching popup on their next dashboard visit (and in /inbox).
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setComposerOpen(false)} data-testid={`section-${section}-alert-cancel`}>
              <X className="mr-1 h-3.5 w-3.5" /> Cancel
            </Button>
            <Button size="sm" onClick={sendUpdateAlert} disabled={sending} className="bg-clay text-white hover:bg-clay-dark" data-testid={`section-${section}-alert-send`}>
              <Send className="mr-1 h-3.5 w-3.5" /> {sending ? "Sending…" : "Send alert"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// One expandable row inside the dialog.
function ExpandableStepRow({ section, ok, label, hint, count, row, details, verified, editing, onStartEdit, onCancelEdit, onSavedEdit, onToggleVerified, onMessageSent, expanded, onToggle }) {
  const renderEditor = () => {
    switch (section) {
      case "profile": return <ProfileEditor details={details} onCancel={onCancelEdit} onSaved={onSavedEdit} />;
      case "enrolment": return <EnrolmentEditor details={details} onCancel={onCancelEdit} onSaved={onSavedEdit} />;
      case "vendors": return <VendorsEditor details={details} onCancel={onCancelEdit} onSaved={onSavedEdit} />;
      case "cchs": return <CchsEditor details={details} onCancel={onCancelEdit} onSaved={onSavedEdit} />;
      default: return null;
    }
  };
  return (
    <li className="overflow-hidden rounded-lg border border-border bg-white" data-testid={`step-row-${section}`}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-2 px-3 py-2 text-left transition hover:bg-sand-50"
        data-testid={`step-row-${section}-toggle`}
      >
        {ok
          ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />}
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-ink-900">
            {label}
            {verified && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[0.55rem] font-bold text-emerald-700" data-testid={`step-row-${section}-verified-chip`}>
                <ShieldCheck className="h-2.5 w-2.5" /> Admin Verified
              </span>
            )}
          </p>
          {hint && <p className="text-[0.65rem] text-ink-700">{hint}</p>}
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[0.6rem] font-semibold ${ok ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"}`}>
          {ok ? `Done${count ? ` · ${count}` : ""}` : "Pending"}
        </span>
        {expanded
          ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-ink-700" />
          : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-ink-700" />}
      </button>
      {expanded && (
        <div className="border-t border-border bg-sand-50 px-3 py-3" data-testid={`step-row-${section}-panel`}>
          {editing ? renderEditor() : <SectionData section={section} details={details} />}
          <SectionActions
            section={section}
            row={row}
            details={details}
            verified={verified}
            editing={editing}
            onStartEdit={onStartEdit}
            onToggleVerified={onToggleVerified}
            onMessageSent={onMessageSent}
          />
        </div>
      )}
    </li>
  );
}

/**
 * Hook + cell: fetches `/api/admin/setup-progress` once and exposes a map
 * by udiseCode + a Pill renderer + a Dialog. Used inside AdminDashboard.
 */
export function useSetupProgress() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  // Per-dialog details + which section is currently expanded + which is in edit mode.
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [editingSection, setEditingSection] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/setup-progress`);
      setRows(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load setup progress");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const byUdise = useMemo(() => {
    const m = {};
    for (const r of rows) m[r.udiseCode] = r;
    return m;
  }, [rows]);

  const openRow = openId ? rows.find((r) => r.userId === openId) : null;

  // Lazy-load details whenever a new dialog opens.
  const reloadDetails = async (uid) => {
    const id = uid || openId;
    if (!id) return;
    setDetailsLoading(true);
    try {
      const r = await axios.get(`${API}/admin/setup-progress/${id}/details`);
      setDetails(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load school details");
    } finally { setDetailsLoading(false); }
  };

  useEffect(() => {
    if (!openId) {
      setDetails(null);
      setExpandedSection(null);
      setEditingSection(null);
      return;
    }
    let cancelled = false;
    setDetailsLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/setup-progress/${openId}/details`);
        if (!cancelled) setDetails(r.data);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || "Failed to load school details");
      } finally { if (!cancelled) setDetailsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [openId]);

  const updateVerifiedFlag = (section, nextVal) => {
    setDetails((d) => d ? { ...d, verifiedSections: { ...d.verifiedSections, [section]: nextVal } } : d);
  };

  const Cell = ({ udiseCode }) => {
    const row = byUdise[udiseCode];
    if (!row) {
      return <span className="text-[0.65rem] text-ink-700">—</span>;
    }
    return (
      <ProgressPill
        percent={row.percent}
        done={row.done}
        total={row.total}
        testId={`admin-setup-progress-${udiseCode}`}
        onClick={() => setOpenId(row.userId)}
      />
    );
  };

  const verifiedSections = details?.verifiedSections || {};

  const DetailDialog = () => (
    <Dialog open={!!openRow} onOpenChange={(o) => !o && setOpenId(null)}>
      <DialogContent data-testid="admin-setup-progress-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2 pr-6">
            <span>Setup Progress</span>
            {openRow && (
              <span className="rounded-full bg-clay/10 px-2.5 py-0.5 text-[0.7rem] font-semibold text-clay">{openRow.percent}%</span>
            )}
          </DialogTitle>
          <DialogDescription>
            {openRow?.schoolName} · UDISE {openRow?.udiseCode}
            {detailsLoading && <span className="ml-2 text-clay">· loading details…</span>}
          </DialogDescription>
        </DialogHeader>

        {openRow && (
          <div className="space-y-3">
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-sand-100">
              <div
                className={`h-full transition-all ${openRow.percent === 100 ? "bg-emerald-500" : openRow.percent >= 50 ? "bg-ochre" : "bg-rose-400"}`}
                style={{ width: `${openRow.percent}%` }}
              />
            </div>
            <p className="text-[0.65rem] text-ink-700">
              Click any section below to view this school's saved data and take action.
            </p>
            <ul className="space-y-2">
              <ExpandableStepRow
                section="profile"
                ok={openRow.steps.profile}
                label="School Profile filled"
                hint={openRow.steps.profile
                  ? `${openRow.profileFields.village}, ${openRow.profileFields.block}, ${openRow.profileFields.district} — ${openRow.profileFields.pincode}`
                  : "District / Block / Village / Post / Pincode all required."}
                row={openRow}
                details={details}
                verified={!!verifiedSections.profile}
                editing={editingSection === "profile"}
                onStartEdit={() => setEditingSection("profile")}
                onCancelEdit={() => setEditingSection(null)}
                onSavedEdit={async () => { await reloadDetails(); await refresh(); setEditingSection(null); }}
                onToggleVerified={(nv) => updateVerifiedFlag("profile", nv)}
                onMessageSent={() => {}}
                expanded={expandedSection === "profile"}
                onToggle={() => { setEditingSection(null); setExpandedSection((s) => s === "profile" ? null : "profile"); }}
              />
              <ExpandableStepRow
                section="enrolment"
                ok={openRow.steps.enrolment}
                label="Class Enrolment recorded"
                hint={openRow.steps.enrolment ? `${openRow.enrolTotal} students total` : "At least one class must have headcount > 0."}
                row={openRow}
                details={details}
                verified={!!verifiedSections.enrolment}
                editing={editingSection === "enrolment"}
                onStartEdit={() => setEditingSection("enrolment")}
                onCancelEdit={() => setEditingSection(null)}
                onSavedEdit={async () => { await reloadDetails(); await refresh(); setEditingSection(null); }}
                onToggleVerified={(nv) => updateVerifiedFlag("enrolment", nv)}
                onMessageSent={() => {}}
                expanded={expandedSection === "enrolment"}
                onToggle={() => { setEditingSection(null); setExpandedSection((s) => s === "enrolment" ? null : "enrolment"); }}
              />
              <ExpandableStepRow
                section="vendors"
                ok={openRow.steps.vendors}
                label="Vendors added"
                hint={openRow.vendorCount > 0 ? `${openRow.vendorCount} vendor(s)` : "Add grocery / vegetable / egg / fuel / ladoo vendors."}
                count={openRow.vendorCount}
                row={openRow}
                details={details}
                verified={!!verifiedSections.vendors}
                editing={editingSection === "vendors"}
                onStartEdit={() => setEditingSection("vendors")}
                onCancelEdit={() => setEditingSection(null)}
                onSavedEdit={async () => { await reloadDetails(); await refresh(); }}
                onToggleVerified={(nv) => updateVerifiedFlag("vendors", nv)}
                onMessageSent={() => {}}
                expanded={expandedSection === "vendors"}
                onToggle={() => { setEditingSection(null); setExpandedSection((s) => s === "vendors" ? null : "vendors"); }}
              />
              <ExpandableStepRow
                section="cchs"
                ok={openRow.steps.cchs}
                label="Cook-cum-Helpers added"
                hint={openRow.cchCount > 0 ? `${openRow.cchCount} cook(s)` : "Add at least one cook."}
                count={openRow.cchCount}
                row={openRow}
                details={details}
                verified={!!verifiedSections.cchs}
                editing={editingSection === "cchs"}
                onStartEdit={() => setEditingSection("cchs")}
                onCancelEdit={() => setEditingSection(null)}
                onSavedEdit={async () => { await reloadDetails(); await refresh(); }}
                onToggleVerified={(nv) => updateVerifiedFlag("cchs", nv)}
                onMessageSent={() => {}}
                expanded={expandedSection === "cchs"}
                onToggle={() => { setEditingSection(null); setExpandedSection((s) => s === "cchs" ? null : "cchs"); }}
              />
            </ul>

            <div className="grid grid-cols-2 gap-2 rounded-xl border border-border bg-sand-50 p-3 text-[0.7rem]">
              <div>
                <p className="text-ink-700">Setup Choice</p>
                <p className="font-semibold text-ink-900">
                  {openRow.setupChoice === "self" ? "Self" : openRow.setupChoice === "admin" ? "Admin / WhatsApp" : "Not picked"}
                </p>
              </div>
              <div>
                <p className="text-ink-700">Account</p>
                <p className={`font-semibold ${openRow.verified ? "text-emerald-700" : "text-amber-700"}`}>
                  {openRow.verified ? "Verified" : "Pending verification"}
                </p>
              </div>
            </div>

            <Button variant="outline" size="sm" onClick={refresh} className="w-full gap-1.5" data-testid="admin-setup-progress-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  return { Cell, DetailDialog, refresh, loading, byUdise };
}
